import { PlaybookDetailsBody } from '@apps/web/components/playbooks/playbook_details_body';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { useAgentPlaybookItems } from '@apps/web/swr_data_fetching/data_fetching_hooks';

/**
 * The playbook rule behind a decision, opened in place.
 *
 * Renders the Playbooks page's own detail body, so a rule read from a
 * citation is the same record as a rule read from settings. The editing
 * controls stay on that page; this is somewhere you check a decision.
 */
export function PlaybookRecordModal({
    organizationId,
    playbookId,
    onClose,
}: {
    readonly organizationId: string;
    readonly playbookId: string;
    readonly onClose: () => void;
}) {
    const { data, isLoading } = useAgentPlaybookItems(organizationId, {
        reason: 'phoebe-chat-learned-playbook-modal',
    });
    const playbook = data?.items.find((item) => item.id === playbookId) ?? null;

    return (
        <Dialog
            open={true}
            onOpenChange={(next) => {
                if (!next) {
                    onClose();
                }
            }}
        >
            <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>
                        {playbook?.title ?? 'Playbook rule'}
                    </DialogTitle>
                    <DialogDescription>
                        {playbook?.trigger_description ??
                            'The rule behind this decision.'}
                    </DialogDescription>
                </DialogHeader>
                {playbook === null ? (
                    <p className="text-muted-foreground text-sm">
                        {isLoading ? (
                            <Skeleton className="h-24 w-full" />
                        ) : (
                            'This rule is no longer in your playbook.'
                        )}
                    </p>
                ) : (
                    <div className="space-y-5">
                        <PlaybookDetailsBody
                            enabled={playbook.enabled}
                            appliesTo={playbook.applies_to}
                            createdByUserName={playbook.created_by_user_name}
                            updatedByUserName={playbook.updated_by_user_name}
                            updatedAt={playbook.updated_at}
                            bodyMarkdown={playbook.body_markdown}
                        />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
